"use client" // クライアントコンポーネントとして宣言

import { LawData, LawContent } from "@/actions/eGov"
import Link from "next/link"

// shadcn/ui アコーディオンコンポーネント
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion"

/**
 * こちらはクライアントコンポーネント。
 * - shadcn/ui の Accordion を使って目次 (章/節) をアコーディオン表示
 * - 同時に 条文本文 (article) も表示する
 */
export default function KenchikuCodeViewerClient({ data }: { data: LawData }) {
  // data.contents にトップ階層（章/節/条）が入っている
  return (
    <div>
      <h2>【目次】</h2>
      <TocList items={data.contents} />

      <h2>【条文】</h2>
      <ContentList items={data.contents} />
    </div>
  )
}

/**
 * 目次表示 (shadcn/ui Accordion)
 */
function TocList({ items }: { items: LawContent[] }) {
  if (!items || items.length === 0) return null

  return (
    <Accordion type="multiple" className="mt-2">
      {items.map((item) => (
        <AccordionItem key={item.id} value={item.id}>
          <AccordionTrigger>{item.title}</AccordionTrigger>

          <AccordionContent>
            {item.children && item.children.length > 0 ? (
              <TocSubList items={item.children} />
            ) : (
              <TocLeafLink item={item} />
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

/**
 * 下位の節や条が混在する場合、再帰的にアコーディオンを継続
 */
function TocSubList({ items }: { items: LawContent[] }) {
  return (
    <Accordion type="multiple" className="pl-3">
      {items.map((sub) => (
        <AccordionItem key={sub.id} value={sub.id}>
          <AccordionTrigger>{sub.title}</AccordionTrigger>
          <AccordionContent>
            {sub.children && sub.children.length > 0 ? (
              <TocSubList items={sub.children} />
            ) : (
              <TocLeafLink item={sub} />
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

/**
 * 子要素なし(=条レベル等)の場合は、アンカーリンクだけ表示
 */
function TocLeafLink({ item }: { item: LawContent }) {
  return (
    <ul>
      <li>
        <Link href={`#${item.id}`}>{item.title}</Link>
      </li>
    </ul>
  )
}

/**
 * 章・節・条を <article> で表示 (本文部)
 */
function ContentList({ items }: { items: LawContent[] }) {
  if (!items || items.length === 0) return null
  return (
    <>
      {items.map((item) => (
        <ArticleOrChapterView key={item.id} content={item} />
      ))}
    </>
  )
}

function ArticleOrChapterView({ content }: { content: LawContent }) {
  const { id, title, paragraphs, children } = content

  return (
    <article
      id={id}
      style={{
        padding: "1rem 0",
        borderBottom: "1px solid #ccc",
        marginBottom: "1rem",
      }}
    >
      <h3>{title}</h3>
      {/* 条の場合: paragraphsに本文が入っている */}
      {paragraphs.map((p, idx) => (
        <p key={idx}>{p}</p>
      ))}

      {/* 下位要素(節や条)も再帰 */}
      {children && children.length > 0 && <ContentList items={children} />}
    </article>
  )
}
